import React, { useState, useEffect } from "react";
import { Container, Alert, Form } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { editProfile, resetProfileFlag } from "../../slices/thunks";
import FeatherIcon from "feather-icons-react";
import avatar from "../../assets/images/users/user-dummy-img.jpg";

const UserProfile = () => {
  const dispatch = useDispatch();

  const [profile,     setProfile]     = useState(null);
  const [isEditing,   setIsEditing]   = useState(false);
  const [nameValue,   setNameValue]   = useState("");
  const [nameError,   setNameError]   = useState("");
  const [nameLoading, setNameLoading] = useState(false);
  const [successMsg,  setSuccessMsg]  = useState("");
  const [errorMsg,    setErrorMsg]    = useState("");

  const userprofileData = createSelector(
    (state) => state.Profile,
    (state) => ({ user: state.user, success: state.success, error: state.error })
  );
  const { user, success, error } = useSelector(userprofileData);

  useEffect(() => {
    const authUser = sessionStorage.getItem("authUser");
    if (authUser) {
      const parsed = JSON.parse(authUser);
      setProfile(parsed.user);
      setNameValue(parsed.user?.username || "");
    }
  }, []);

  useEffect(() => {
    if (success && user) {
      const authUser = sessionStorage.getItem("authUser");
      if (authUser) {
        const parsed = JSON.parse(authUser);
        parsed.user.username = user.username || user.first_name;
        sessionStorage.setItem("authUser", JSON.stringify(parsed));
        setProfile((prev) => ({ ...prev, username: parsed.user.username }));
      }
      flash("success", "Username updated successfully!");
      setIsEditing(false);
      setTimeout(() => dispatch(resetProfileFlag()), 3000);
    }
    if (error) flash("error", error);
  }, [success, error]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (type, msg) => {
    if (type === "success") { setSuccessMsg(msg); setErrorMsg(""); }
    else                    { setErrorMsg(msg);   setSuccessMsg(""); }
    setTimeout(() => { setSuccessMsg(""); setErrorMsg(""); }, 4000);
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!nameValue.trim()) { setNameError("Username is required"); return; }
    setNameError("");
    setNameLoading(true);
    try {
      await dispatch(editProfile({ first_name: nameValue.trim(), idx: profile?.id }));
    } finally {
      setNameLoading(false);
    }
  };

  document.title = "Profile | SOC Platform";
  if (!profile) return null;

  const initials = (profile.username || "U").slice(0, 2).toUpperCase();

  const card = {
    background:   "var(--vz-secondary-bg, #fff)",
    border:       "1px solid var(--vz-border-color)",
    borderRadius: 12,
    boxShadow:    "0 1px 4px rgba(0,0,0,0.06)",
  };

  return (
    <div className="page-content">
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        .profile-section { animation: fadeUp .3s ease both; }
        .profile-section:nth-child(2) { animation-delay:.06s }
        .profile-section:nth-child(3) { animation-delay:.12s }
        .profile-section:nth-child(4) { animation-delay:.18s }
        .edit-input:focus { outline: none; border-color: var(--vz-primary) !important; box-shadow: 0 0 0 3px rgba(var(--vz-primary-rgb),.12) !important; }
      `}</style>

      <Container fluid>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>

          {errorMsg   && <Alert color="danger"  className="mb-3 profile-section">{errorMsg}</Alert>}
          {successMsg && (
            <div className="profile-section" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "rgba(var(--vz-success-rgb),.1)", border: "1px solid rgba(var(--vz-success-rgb),.3)", borderRadius: 10, marginBottom: 16, fontSize: 13, color: "var(--vz-success)", fontWeight: 500 }}>
              <FeatherIcon icon="check-circle" size={16} />
              {successMsg}
            </div>
          )}

          {/* ── Hero card ─────────────────────────────────── */}
          <div className="profile-section" style={{ ...card, marginBottom: 16, overflow: "hidden" }}>
            {/* Banner */}
            <div style={{ height: 80, background: "linear-gradient(135deg, rgba(var(--vz-primary-rgb),.15) 0%, rgba(var(--vz-primary-rgb),.05) 100%)", borderBottom: "1px solid var(--vz-border-color)", position: "relative" }}>
              <div style={{ position: "absolute", right: 20, top: 16, display: "flex", gap: 6, opacity: .2 }}>
                {[40,24,16].map((s,i) => <div key={i} style={{ width: s, height: s, borderRadius: "50%", border: "2px solid var(--vz-primary)" }} />)}
              </div>
            </div>

            <div style={{ padding: "0 24px 24px" }}>
              {/* Avatar */}
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginTop: -28 }}>
                <div style={{ position: "relative" }}>
                  {profile.user_image ? (
                    <img
                      src={profile.user_image} alt="profile"
                      onError={e => { e.target.src = avatar; }}
                      style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", border: "3px solid var(--vz-secondary-bg)", boxShadow: "0 2px 8px rgba(0,0,0,.12)" }}
                    />
                  ) : (
                    <div style={{ width: 72, height: 72, borderRadius: "50%", background: "var(--vz-primary)", border: "3px solid var(--vz-secondary-bg)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,.12)" }}>
                      {initials}
                    </div>
                  )}
                  {/* Online dot */}
                  <span style={{ position: "absolute", bottom: 4, right: 4, width: 14, height: 14, borderRadius: "50%", background: "#22c55e", border: "2px solid var(--vz-secondary-bg)" }} />
                </div>

                {/* Badges */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", paddingTop: 36 }}>
                  {profile.role && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: "rgba(var(--vz-primary-rgb),.12)", color: "var(--vz-primary)", border: "1px solid rgba(var(--vz-primary-rgb),.25)", textTransform: "uppercase", letterSpacing: ".04em" }}>
                      <FeatherIcon icon="shield" size={11} />
                      {profile.role}
                    </span>
                  )}
                  {profile.company_name && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 20, background: "var(--vz-light, rgba(0,0,0,.04))", color: "var(--vz-secondary-color)", border: "1px solid var(--vz-border-color)" }}>
                      <FeatherIcon icon="briefcase" size={11} />
                      {profile.company_name}
                    </span>
                  )}
                </div>
              </div>

              {/* Name + email */}
              <div style={{ marginTop: 14 }}>
                <h4 style={{ fontWeight: 800, fontSize: 20, color: "var(--vz-body-color)", margin: "0 0 4px" }}>
                  {profile.username}
                </h4>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--vz-secondary-color)", fontSize: 13 }}>
                  <FeatherIcon icon="mail" size={13} />
                  {profile.email}
                </div>
              </div>
            </div>
          </div>

          {/* ── Edit username ──────────────────────────────── */}
          <div className="profile-section" style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--vz-secondary-color)", margin: "0 0 8px 2px" }}>
              Display Name
            </p>
            <div style={card}>
              <div style={{ padding: "18px 20px" }}>
                {!isEditing ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(var(--vz-primary-rgb),.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FeatherIcon icon="user" size={16} style={{ color: "var(--vz-primary)" }} />
                      </div>
                      <div>
                        <p style={{ fontSize: 11, color: "var(--vz-secondary-color)", margin: 0 }}>Username</p>
                        <p style={{ fontSize: 14, fontWeight: 600, color: "var(--vz-body-color)", margin: 0 }}>{profile.username}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setNameValue(profile.username); setIsEditing(true); }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "transparent", color: "var(--vz-primary)", border: "1px solid var(--vz-primary)", cursor: "pointer", transition: "all .15s" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "var(--vz-primary)"; e.currentTarget.style.color = "#fff"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--vz-primary)"; }}
                    >
                      <FeatherIcon icon="edit-2" size={13} />
                      Edit
                    </button>
                  </div>
                ) : (
                  <Form onSubmit={handleUpdateName}>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--vz-secondary-color)", display: "block", marginBottom: 6 }}>New Username</label>
                      <input
                        type="text"
                        className="edit-input"
                        placeholder="Enter your username"
                        value={nameValue}
                        onChange={e => { setNameValue(e.target.value); setNameError(""); }}
                        style={{ width: "100%", height: 40, padding: "0 12px", border: `1px solid ${nameError ? "var(--vz-danger)" : "var(--vz-border-color)"}`, borderRadius: 8, fontSize: 13, color: "var(--vz-body-color)", background: "var(--vz-card-bg, #fff)", transition: "border .15s" }}
                      />
                      {nameError && <p style={{ fontSize: 12, color: "var(--vz-danger)", margin: "4px 0 0" }}>{nameError}</p>}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button type="button"
                        onClick={() => { setIsEditing(false); setNameError(""); }}
                        style={{ padding: "7px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: "transparent", color: "var(--vz-secondary-color)", border: "1px solid var(--vz-border-color)", cursor: "pointer" }}>
                        Cancel
                      </button>
                      <button type="submit" disabled={nameLoading}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600, background: nameLoading ? "var(--vz-secondary-color)" : "var(--vz-primary)", color: "#fff", border: "none", cursor: nameLoading ? "not-allowed" : "pointer", opacity: nameLoading ? .7 : 1 }}>
                        {nameLoading
                          ? <><span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12, borderWidth: 2 }} />Saving...</>
                          : <><FeatherIcon icon="check" size={13} />Save Changes</>
                        }
                      </button>
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </div>

          {/* ── Account details ────────────────────────────── */}
          <div className="profile-section" style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: "var(--vz-secondary-color)", margin: "0 0 8px 2px" }}>
              Account Details
            </p>
            <div style={card}>
              {[
                { icon: "mail",      label: "Email",        value: profile.email       },
                { icon: "briefcase", label: "Company",      value: profile.company_name },
                { icon: "shield",    label: "Role",         value: profile.role         },
              ].map(({ icon, label, value }, i, arr) => (
                <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: i < arr.length - 1 ? "1px solid var(--vz-border-color)" : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: "rgba(var(--vz-primary-rgb),.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <FeatherIcon icon={icon} size={14} style={{ color: "var(--vz-primary)" }} />
                    </div>
                    <span style={{ fontSize: 13, color: "var(--vz-secondary-color)" }}>{label}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--vz-body-color)" }}>{value || "—"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Security notice ────────────────────────────── */}
          <div className="profile-section">
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "rgba(var(--vz-primary-rgb),.04)", border: "1px solid rgba(var(--vz-primary-rgb),.12)", borderRadius: 10, fontSize: 12, color: "var(--vz-secondary-color)" }}>
              <FeatherIcon icon="lock" size={14} style={{ color: "var(--vz-primary)", flexShrink: 0 }} />
              Your account is secured and access is managed by your organization administrator.
            </div>
          </div>

        </div>
      </Container>
    </div>
  );
};

export default UserProfile;